import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Globe, Check } from 'lucide-react';
import { useLanguage, languages } from '../context/LanguageContext';

export function LanguageSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const { language, setLanguage } = useLanguage();

  const currentLanguage = languages.find(l => l.code === language);

  return (
    <div className="relative z-50">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#235347]/80 to-[#163832]/80 backdrop-blur-xl rounded-lg border border-[#8EB69B]/30 text-white shadow-lg hover:shadow-xl transition-all"
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <Globe className="h-4 w-4 text-[#C9A961]" />
        </motion.div>
        <span className="hidden md:block text-sm">{currentLanguage?.nativeName}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="text-[#8EB69B] text-xs"
        >
          ▼
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm -z-10"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown Menu */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full mt-3 right-0 w-72 bg-gradient-to-br from-[#051F20] to-[#0B2B26] backdrop-blur-2xl rounded-2xl border-2 border-[#8EB69B]/30 shadow-2xl overflow-hidden"
            >
              {/* Premium Header */}
              <div className="bg-gradient-to-r from-[#235347] to-[#163832] p-4 border-b border-[#8EB69B]/20">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Globe className="h-4 w-4 text-[#C9A961]" />
                  Select Language
                </h3>
                <p className="text-[#DAF1DE]/70 text-sm mt-1">Choose your preferred language</p>
              </div>

              {/* Language Options */}
              <div className="p-2 max-h-96 overflow-y-auto custom-scrollbar">
                {languages.map((lang, index) => (
                  <motion.button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code as any);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center justify-between p-4 rounded-xl transition-all group ${
                      language === lang.code
                        ? 'bg-gradient-to-r from-[#C9A961]/20 to-[#8EB69B]/20 border-2 border-[#C9A961]/50'
                        : 'hover:bg-[#235347]/30 border-2 border-transparent'
                    }`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex items-center gap-4">
                      {/* Language Icon/Flag Placeholder */}
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center text-xl font-semibold ${
                        language === lang.code
                          ? 'bg-gradient-to-br from-[#C9A961] to-[#8EB69B] text-white shadow-lg'
                          : 'bg-[#235347]/50 text-[#DAF1DE]/70 group-hover:bg-[#235347]'
                      }`}>
                        {lang.nativeName.charAt(0)}
                      </div>
                      
                      <div className="text-left">
                        <p className={`font-semibold ${
                          language === lang.code ? 'text-white' : 'text-[#DAF1DE] group-hover:text-white'
                        }`}>
                          {lang.nativeName}
                        </p>
                        <p className="text-[#8EB69B]/80 text-sm">{lang.name}</p>
                      </div>
                    </div>

                    {language === lang.code && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="h-6 w-6 bg-[#C9A961] rounded-full flex items-center justify-center"
                      >
                        <Check className="h-4 w-4 text-white" />
                      </motion.div>
                    )}
                  </motion.button>
                ))}
              </div>

              {/* Premium Footer */}
              <div className="bg-gradient-to-r from-[#163832] to-[#051F20] p-3 border-t border-[#8EB69B]/20">
                <p className="text-[#DAF1DE]/60 text-xs text-center">
                  Language settings saved automatically
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(22, 56, 50, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #C9A961, #8EB69B);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #8EB69B, #C9A961);
        }
      `}</style>
    </div>
  );
}